import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, startOfDay, endOfDay, startOfWeek, startOfMonth, subDays } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon, Download, CreditCard, AlertCircle, UserCheck, Undo2, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { exportToCsv } from "@/lib/csvExport";
import { exportToSie } from "@/lib/reportExport";
import { toast } from "sonner";
import BookingDetailModal from "@/components/booking/BookingDetailModal";
import MatchiSettledRow from "@/components/admin/MatchiSettledRow";
import { splitMatchiPayments } from "@/lib/matchiRevenue";

type QuickRange = "today" | "yesterday" | "this_week" | "this_month" | "custom";

interface EnrichedPayment {
  id: string;
  user_id: string;
  booking_id: string;
  amount: number;                      // legacy kronor
  amount_minor: number | null;         // öre (Sprint D)
  currency: string;
  status: string;
  payment_method: string | null;
  paid_at: string | null;
  created_at: string;
  receipt_number: string | null;
  external_reference: string | null;
  marked_paid_by: string | null;
  notes: string | null;
  booking: { id: string; start_time: string; court_id: string; courts: { name: string } | null } | null;
  payer: { full_name: string | null };
  marker?: { full_name: string | null };
}

export default function PaymentReconciliation() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { user } = useAuth();
  const { paymentMethodOptions: paymentMethods } = usePaymentMethods();
  const qc = useQueryClient();

  const [quickRange, setQuickRange] = useState<QuickRange>("today");
  const [customFrom, setCustomFrom] = useState<Date>(new Date());
  const [customTo, setCustomTo] = useState<Date>(new Date());
  const [methodFilter, setMethodFilter] = useState<string | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  // Mark-paid dialog state
  const [markPaymentId, setMarkPaymentId] = useState<string | null>(null);
  const [markMethod, setMarkMethod] = useState("");
  const [markReceiptNumber, setMarkReceiptNumber] = useState("");
  const [markExternalRef, setMarkExternalRef] = useState("");
  const [markNotes, setMarkNotes] = useState("");

  const filteredPaymentMethods = paymentMethods.filter(
    (m) => m.value !== "unpaid" && m.value !== "link"
  );

  const getMethodLabel = (value: string | null) =>
    paymentMethods.find((m) => m.value === value)?.label ?? value ?? t("reports.reconciliation.unknown");

  // Compute date range
  const { rangeStart, rangeEnd } = useMemo(() => {
    const now = new Date();
    switch (quickRange) {
      case "today":
        return { rangeStart: startOfDay(now), rangeEnd: endOfDay(now) };
      case "yesterday": {
        const y = subDays(now, 1);
        return { rangeStart: startOfDay(y), rangeEnd: endOfDay(y) };
      }
      case "this_week":
        return { rangeStart: startOfWeek(now, { weekStartsOn: 1 }), rangeEnd: endOfDay(now) };
      case "this_month":
        return { rangeStart: startOfMonth(now), rangeEnd: endOfDay(now) };
      case "custom":
        return { rangeStart: startOfDay(customFrom), rangeEnd: endOfDay(customTo) };
    }
  }, [quickRange, customFrom, customTo]);

  const rangeStartIso = rangeStart.toISOString();
  const rangeEndIso = rangeEnd.toISOString();

  // All payments in range (paid ones by paid_at, all by created_at for unpaid)
  const { data: paidPayments = [] } = useQuery({
    queryKey: ["reconciliation-paid", organization?.id, rangeStartIso, rangeEndIso],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_payments")
        .select("*, booking:bookings(id, start_time, court_id, courts(name))")
        .eq("organization_id", organization!.id)
        .in("status", ["paid", "manual"])
        .gte("paid_at", rangeStartIso)
        .lte("paid_at", rangeEndIso)
        .order("paid_at", { ascending: false });
      if (!data) return [];
      // Enrich with profile names
      const userIds = [...new Set(data.map((p) => p.user_id).filter(Boolean))];
      const markerIds = [...new Set(data.map((p) => (p as Record<string, unknown>).marked_paid_by as string).filter(Boolean))];
      const allIds = [...new Set([...userIds, ...markerIds])];
      const { data: profiles } = allIds.length > 0
        ? await supabase.from("public_profiles" as any).select("id, full_name").in("id", allIds)
        : { data: [] as { id: string; full_name: string | null }[] };
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p.full_name]));
      return data.map((p) => ({
        ...p,
        payer: { full_name: profileMap.get(p.user_id) ?? null },
        marker: { full_name: profileMap.get((p as Record<string, unknown>).marked_paid_by as string) ?? null },
      })) as unknown as EnrichedPayment[];
    },
    enabled: !!organization?.id,
  });

  // Unpaid payments for bookings in range
  const { data: unpaidPayments = [] } = useQuery({
    queryKey: ["reconciliation-unpaid", organization?.id, rangeStartIso, rangeEndIso],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_payments")
        .select("*, booking:bookings(id, start_time, court_id, courts(name))")
        .eq("organization_id", organization!.id)
        .not("status", "in", '("paid","manual")')
        .gte("created_at", rangeStartIso)
        .lte("created_at", rangeEndIso)
        .order("created_at", { ascending: false });
      if (!data) return [];
      const userIds = [...new Set(data.map((p) => p.user_id).filter(Boolean))];
      const { data: profiles } = userIds.length > 0
        ? await supabase.from("public_profiles" as any).select("id, full_name").in("id", userIds)
        : { data: [] as { id: string; full_name: string | null }[] };
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p.full_name]));
      return data.map((p) => ({
        ...p,
        payer: { full_name: profileMap.get(p.user_id) ?? null },
      })) as unknown as EnrichedPayment[];
    },
    enabled: !!organization?.id,
  });

  // Refunded payments in range
  const { data: refundedPayments = [] } = useQuery({
    queryKey: ["reconciliation-refunded", organization?.id, rangeStartIso, rangeEndIso],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_payments")
        .select("*, booking:bookings(id, start_time, court_id, courts(name))")
        .eq("organization_id", organization!.id)
        .eq("status", "refunded")
        .gte("created_at", rangeStartIso)
        .lte("created_at", rangeEndIso)
        .order("created_at", { ascending: false });
      if (!data) return [];
      const userIds = [...new Set(data.map((p) => p.user_id).filter(Boolean))];
      const { data: profiles } = userIds.length > 0
        ? await supabase.from("public_profiles" as any).select("id, full_name").in("id", userIds)
        : { data: [] as { id: string; full_name: string | null }[] };
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p.full_name]));
      return data.map((p) => ({
        ...p,
        payer: { full_name: profileMap.get(p.user_id) ?? null },
      })) as unknown as EnrichedPayment[];
    },
    enabled: !!organization?.id,
  });

  // Summary calculations — Sprint D aggregates: prefer amount_minor.
  const sumMinor = (arr: EnrichedPayment[]) =>
    arr.reduce((s, p) => s + (resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0), 0);
  // C3/BES-004: "Totalt inkommet" är Boplys avstämning — MATCHi-avräknade rader
  // ligger i deras utbetalningsunderlag och särredovisas därför här. Raderna finns
  // kvar i listan och i metoduppdelningen nedan; det är bara totalen som skiljer.
  const { boply: boplyPaid, matchi: matchiPaid } = splitMatchiPayments(paidPayments);
  const totalPaidMinor = sumMinor(boplyPaid);
  const matchiSettledMinor = sumMinor(matchiPaid);
  const totalUnpaidMinor = sumMinor(unpaidPayments);
  const totalRefundedMinor = sumMinor(refundedPayments);
  const _totalPaid = Math.round(totalPaidMinor / 100);
  const _totalUnpaid = Math.round(totalUnpaidMinor / 100);
  const _totalRefunded = Math.round(totalRefundedMinor / 100);
  const transactionCount = paidPayments.length;
  const manualCount = paidPayments.filter((p) => p.marked_paid_by != null).length;

  // Method breakdown — Sprint D: sum in öre internally.
  const methodBreakdown = useMemo(() => {
    const map: Record<string, { count: number; sumMinor: number; manual: number }> = {};
    for (const p of paidPayments) {
      const m = p.payment_method ?? "unknown";
      if (!map[m]) map[m] = { count: 0, sumMinor: 0, manual: 0 };
      map[m].count++;
      map[m].sumMinor += resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0;
      if (p.marked_paid_by) map[m].manual++;
    }
    return Object.entries(map)
      .map(([method, stats]) => ({ method, count: stats.count, manual: stats.manual, sum: Math.round(stats.sumMinor / 100), sumMinor: stats.sumMinor }))
      .sort((a, b) => b.sumMinor - a.sumMinor);
  }, [paidPayments]);

  // Filtered paid list
  const filteredPaid = methodFilter
    ? paidPayments.filter((p) => p.payment_method === methodFilter)
    : paidPayments;

  // CSV export
  const handleExport = () => {
    const rows = filteredPaid.map((p) => ({
      datum: p.paid_at ? format(new Date(p.paid_at), "yyyy-MM-dd") : "",
      tid: p.paid_at ? format(new Date(p.paid_at), "HH:mm") : "",
      bana: p.booking?.courts?.name ?? "",
      spelare: p.payer?.full_name ?? "",
      belopp_kr: p.amount.toFixed(2),
      metod: getMethodLabel(p.payment_method),
      kvittonr: p.receipt_number ?? "",
      referens: p.external_reference ?? "",
      admin: p.marker?.full_name ?? "",
    }));
    exportToCsv(`avstamning_${format(rangeStart, "yyyy-MM-dd")}.csv`, rows);
  };

  // Mark paid mutation
  const markPaidMutation = useMutation({
    mutationFn: async () => {
      if (!markPaymentId || !markMethod || !user) throw new Error(t("reports.reconciliation.missingData"));
      const payment = unpaidPayments.find((p) => p.id === markPaymentId);
      if (!payment) throw new Error(t("reports.reconciliation.paymentNotFound"));
      const methodLabel = filteredPaymentMethods.find((m) => m.value === markMethod)?.label ?? markMethod;

      const { error } = await supabase
        .from("booking_payments")
        .update({
          status: "paid",
          payment_method: markMethod,
          paid_at: new Date().toISOString(),
          receipt_number: markReceiptNumber || null,
          external_reference: markExternalRef || null,
          marked_paid_by: user.id,
          notes: markNotes || null,
        } as Record<string, unknown>)
        .eq("id", markPaymentId);
      if (error) throw error;

      await supabase.from("booking_activity_log" as any).insert({
        booking_id: payment.booking_id,
        user_id: user.id,
        action: "payment_received",
        actor_type: "admin",
        description: `Betalning registrerad: ${payment.amount} kr via ${methodLabel}${markReceiptNumber ? ` (kvitto: ${markReceiptNumber})` : ""}`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reconciliation-paid"] });
      qc.invalidateQueries({ queryKey: ["reconciliation-unpaid"] });
      toast.success(t("reports.reconciliation.paymentRegistered"));
      closeMarkDialog();
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("reports.reconciliation.couldNotRegister")); },
  });

  const openMarkDialog = (paymentId: string) => {
    setMarkPaymentId(paymentId);
    setMarkMethod("");
    setMarkReceiptNumber("");
    setMarkExternalRef("");
    setMarkNotes("");
  };

  const closeMarkDialog = () => {
    setMarkPaymentId(null);
  };

  const markDialogPayment = markPaymentId ? unpaidPayments.find((p) => p.id === markPaymentId) : null;

  const quickButtons: { key: QuickRange; label: string }[] = [
    { key: "today", label: t("reports.reconciliation.today") },
    { key: "yesterday", label: t("reports.reconciliation.yesterday") },
    { key: "this_week", label: t("reports.reconciliation.thisWeek") },
    { key: "this_month", label: t("reports.reconciliation.thisMonth") },
    { key: "custom", label: t("reports.reconciliation.custom") },
  ];

  return (
    <div className="space-y-5">
      {/* Date filter */}
      <div className="flex flex-wrap items-center gap-2">
        {quickButtons.map((b) => (
          <Badge
            key={b.key}
            variant={quickRange === b.key ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setQuickRange(b.key)}
          >
            {b.label}
          </Badge>
        ))}
        {quickRange === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 h-8">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {format(customFrom, "d MMM", { locale: sv })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customFrom}
                  onSelect={(d) => d && setCustomFrom(d)}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <span className="text-sm text-muted-foreground">—</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 h-8">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {format(customTo, "d MMM", { locale: sv })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customTo}
                  onSelect={(d) => d && setCustomTo(d)}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.reconciliation.totalReceived")}</p>
            <p className="text-2xl font-bold mt-1">{formatPrice({ valueMinor: totalPaidMinor, currency: "SEK" })}</p>
          </CardContent>
        </Card>
        {matchiSettledMinor > 0 && (
          <Card>
            <CardContent className="p-4">
              <MatchiSettledRow valueMinor={matchiSettledMinor} variant="card" />
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.reconciliation.unpaid")}</p>
            <p className="text-2xl font-bold mt-1 text-destructive">{formatPrice({ valueMinor: totalUnpaidMinor, currency: "SEK" })}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.reconciliation.transactions")}</p>
            <p className="text-2xl font-bold mt-1">{transactionCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.reconciliation.ofWhichManual")}</p>
            <p className="text-2xl font-bold mt-1">{manualCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.reconciliation.refunds")}</p>
            <p className="text-2xl font-bold mt-1 text-orange-500">{formatPrice({ valueMinor: totalRefundedMinor, currency: "SEK" })}</p>
            <p className="text-xs text-muted-foreground">{refundedPayments.length} st</p>
          </CardContent>
        </Card>
      </div>

      {/* Method breakdown */}
      {methodBreakdown.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-3">{t("reports.reconciliation.byPaymentMethod")}</h3>
            <div className="space-y-1.5">
              <div className="grid grid-cols-4 text-xs text-muted-foreground font-medium border-b pb-1.5">
                <span>{t("reports.reconciliation.method")}</span>
                <span className="text-right">{t("reports.reconciliation.count")}</span>
                <span className="text-right">{t("reports.reconciliation.sum")}</span>
                <span className="text-right">{t("reports.reconciliation.manual")}</span>
              </div>
              {methodBreakdown.map((row) => (
                <div key={row.method} className="grid grid-cols-4 text-sm py-1 border-b border-border/50 last:border-0">
                  <span>{getMethodLabel(row.method)}</span>
                  <span className="text-right">{row.count}</span>
                  <span className="text-right font-medium">{formatPrice({ valueMinor: row.sumMinor, currency: "SEK" })}</span>
                  <span className="text-right text-muted-foreground">{row.manual}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{t("reports.reconciliation.transactions")}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => exportToSie(`sie_${format(rangeStart, "yyyy-MM-dd")}.se`, filteredPaid)}>
              <FileText className="h-3.5 w-3.5" />
              {t("reports.reconciliation.sieFile")}
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={handleExport}>
              <Download className="h-3.5 w-3.5" />
              {t("reports.reconciliation.exportCsv")}
            </Button>
          </div>
        </div>

        {/* Method filter chips */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <Badge
            variant={methodFilter === null ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setMethodFilter(null)}
          >
            {t("reports.reconciliation.all")}
          </Badge>
          {methodBreakdown.map((row) => (
            <Badge
              key={row.method}
              variant={methodFilter === row.method ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setMethodFilter(row.method === methodFilter ? null : row.method)}
            >
              {getMethodLabel(row.method)} ({row.count})
            </Badge>
          ))}
        </div>

        <ScrollArea className="max-h-[400px]">
          <div className="space-y-1">
            {filteredPaid.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("reports.reconciliation.noPaymentsInRange")}</p>
            ) : (
              filteredPaid.map((p) => (
                <div
                  key={p.id}
                  className="grid grid-cols-[50px_1fr_1fr_80px_auto_auto] gap-2 items-center text-sm py-2 px-2 rounded-md hover:bg-muted/50 cursor-pointer border-b border-border/30"
                  onClick={() => p.booking?.id && setSelectedBookingId(p.booking.id)}
                >
                  <span className="text-muted-foreground font-mono text-xs">
                    {p.paid_at ? format(new Date(p.paid_at), "HH:mm") : "—"}
                  </span>
                  <span className="truncate">
                    {p.booking?.courts?.name ?? "—"}
                    {p.booking?.start_time && (
                      <span className="text-muted-foreground ml-1 text-xs">
                        {format(new Date(p.booking.start_time), "HH:mm")}
                      </span>
                    )}
                  </span>
                  <span className="truncate">{p.payer?.full_name ?? "—"}</span>
                  <span className="text-right font-medium">{formatPrice(resolveMoney(p.amount_minor, p.amount, p.currency) ?? { valueMinor: 0, currency: "SEK" })}</span>
                  <Badge variant="secondary" className="text-[10px] h-5 whitespace-nowrap">
                    {getMethodLabel(p.payment_method)}
                  </Badge>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground min-w-[100px]">
                    {p.receipt_number && <span>#{p.receipt_number}</span>}
                    {p.external_reference && <span>Ref: {p.external_reference}</span>}
                    {p.marker?.full_name && (
                      <span className="flex items-center gap-0.5">
                        <UserCheck className="h-3 w-3" />
                        {p.marker.full_name}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Refunded section */}
      {refundedPayments.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <Undo2 className="h-4 w-4 text-orange-500" />
            {t("reports.reconciliation.refunds")} ({refundedPayments.length})
          </h3>
          <div className="space-y-1.5">
            {refundedPayments.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border p-2.5 cursor-pointer hover:bg-muted/50"
                onClick={() => p.booking?.id && setSelectedBookingId(p.booking.id)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm font-mono text-muted-foreground">
                    {p.created_at ? format(new Date(p.created_at), "d MMM HH:mm", { locale: sv }) : "—"}
                  </span>
                  <span className="text-sm truncate">{p.booking?.courts?.name ?? "—"}</span>
                  <span className="text-sm truncate">{p.payer?.full_name ?? "—"}</span>
                </div>
                <span className="text-sm font-medium text-orange-500">-{formatPrice(resolveMoney(p.amount_minor, p.amount, p.currency) ?? { valueMinor: 0, currency: "SEK" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unpaid section */}
      {unpaidPayments.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4 text-destructive" />
            {t("reports.reconciliation.unpaidBookings")} ({unpaidPayments.length})
          </h3>
          <div className="space-y-1.5">
            {unpaidPayments.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between rounded-lg border p-2.5"
              >
                <div
                  className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  onClick={() => p.booking?.id && setSelectedBookingId(p.booking.id)}
                >
                  <span className="text-sm font-mono text-muted-foreground">
                    {p.booking?.start_time ? format(new Date(p.booking.start_time), "HH:mm") : "—"}
                  </span>
                  <span className="text-sm truncate">{p.booking?.courts?.name ?? "—"}</span>
                  <span className="text-sm truncate">{p.payer?.full_name ?? "—"}</span>
                  <span className="text-sm font-medium ml-auto mr-2">{formatPrice(resolveMoney(p.amount_minor, p.amount, p.currency) ?? { valueMinor: 0, currency: "SEK" })}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1 shrink-0"
                  onClick={() => openMarkDialog(p.id)}
                >
                  <CreditCard className="h-3 w-3" />
                  {t("reports.reconciliation.markPaid")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking detail modal */}
      {selectedBookingId && (
        <BookingDetailModal
          open={!!selectedBookingId}
          onOpenChange={(v) => { if (!v) setSelectedBookingId(null); }}
          bookingId={selectedBookingId}
        />
      )}

      {/* Mark paid dialog */}
      <Dialog open={!!markPaymentId} onOpenChange={(v) => { if (!v) closeMarkDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("reports.reconciliation.registerPayment")}</DialogTitle>
          </DialogHeader>
          {markDialogPayment && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm font-medium">{markDialogPayment.payer?.full_name ?? t("reports.reconciliation.player")}</p>
                <p className="text-lg font-bold">{formatPrice(resolveMoney(markDialogPayment.amount_minor, markDialogPayment.amount, markDialogPayment.currency) ?? { valueMinor: 0, currency: "SEK" })}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("reports.reconciliation.paymentMethod")} <span className="text-destructive">*</span></Label>
                <Select value={markMethod} onValueChange={setMarkMethod}>
                  <SelectTrigger><SelectValue placeholder={t("reports.reconciliation.selectMethod")} /></SelectTrigger>
                  <SelectContent>
                    {filteredPaymentMethods.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("reports.reconciliation.receiptNumber")}</Label>
                <Input value={markReceiptNumber} onChange={(e) => setMarkReceiptNumber(e.target.value)} placeholder={t("reports.reconciliation.receiptPlaceholder")} />
              </div>
              <div className="space-y-2">
                <Label>{t("reports.reconciliation.externalReference")}</Label>
                <Input value={markExternalRef} onChange={(e) => setMarkExternalRef(e.target.value)} placeholder={t("reports.reconciliation.externalRefPlaceholder")} />
              </div>
              <div className="space-y-2">
                <Label>{t("reports.reconciliation.note")}</Label>
                <Input value={markNotes} onChange={(e) => setMarkNotes(e.target.value)} placeholder={t("reports.reconciliation.notePlaceholder")} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeMarkDialog}>{t("reports.reconciliation.cancel")}</Button>
            <Button
              onClick={() => markPaidMutation.mutate()}
              disabled={!markMethod || markPaidMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {t("reports.reconciliation.registerPayment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
